import type { WorkflowPhaseStep } from '@erp/rpc';
import type { Span, SpanStatus } from './types';

// ---------------------------------------------------------------------------
// A task loop's setup/teardown workflow phase, opened up into the steps its workflow ran.
//
// The operator actions a setup workflow drives are recorded against the LOOP run, so `buildSpans`
// already gathers them under the phase by time window — but as one flat list, where "Login to
// Remote Desktop" and "Log in to Famous" read as a single run of clicks. The workflow's own step
// runs (`getRunTimeline.workflowPhaseSteps`) give each stretch of that list its name and its real
// start and end.
// ---------------------------------------------------------------------------

function stepStatus(status: string): SpanStatus {
  if (status === 'failed' || status === 'timed_out') return 'failed';
  if (status === 'running' || status === 'pending' || status === 'queued') {
    return 'running';
  }
  return 'completed';
}

/** A step span, plus whether its step ever started — only a started step has a window to place
 * rows by. */
type PhaseStep = { span: Span; started: boolean };

function stepSpan(step: WorkflowPhaseStep, runStart: number): PhaseStep {
  const started = step.startedAt ? Date.parse(step.startedAt) : Number.NaN;
  const completed = step.completedAt
    ? Date.parse(step.completedAt)
    : Number.NaN;
  const measured =
    step.duration != null && step.duration > 0
      ? step.duration
      : completed > started
        ? completed - started
        : null;
  const hasStarted = Number.isFinite(started);
  return {
    started: hasStarted,
    span: {
      id: step.id,
      type: 'tool',
      name: step.name,
      isWorkflowStep: true,
      status: stepStatus(step.status),
      startMs: hasStarted ? Math.max(0, started - runStart) : 0,
      durMs: measured,
      timedDurMs: measured,
      children: [],
    },
  };
}

/** The workflow step an operator row belongs to: the one it names as its parent, else the STARTED
 * one whose recorded window it fell in. A row the login step writes names no parent, which is why
 * the window is needed at all; a step that never started has no window (it would sit at 0 and run
 * forever) and so is reachable only by name. */
function owningStep(child: Span, steps: PhaseStep[]): Span | undefined {
  const parent = child.toolInput?.parentToolCallId;
  const named = steps.find((step) => step.span.id === parent);
  if (named) return named.span;
  return steps.find(({ span, started }) => {
    if (!started) return false;
    const end = span.startMs + (span.timedDurMs ?? Number.POSITIVE_INFINITY);
    return child.startMs >= span.startMs && child.startMs <= end;
  })?.span;
}

/**
 * Re-home a workflow phase's children under the steps that produced them. Rows no step claims stay
 * directly under the phase, in time order. No-op for a phase with no recorded steps — every run
 * recorded before steps were served, and every phase that isn't a workflow.
 */
export function nestUnderWorkflowSteps(
  phase: Span,
  steps: readonly WorkflowPhaseStep[],
  runStart: number,
): void {
  const mine: PhaseStep[] = [];
  for (const step of steps) {
    if (step.phaseStepRunId === phase.id) mine.push(stepSpan(step, runStart));
  }
  if (mine.length === 0) return;
  const loose: Span[] = [];
  for (const child of phase.children ?? []) {
    const owner = owningStep(child, mine);
    if (owner) owner.children?.push(child);
    else loose.push(child);
  }
  phase.children = [...mine.map((step) => step.span), ...loose].sort(
    (a, b) => a.startMs - b.startMs,
  );
}
